// Define the route interface
export interface RouteInfo {
  key: string;
  path: string;
  name: string;
  nameKey?: string; // Translation key for the route name (uses 'header' namespace)
  parent?: string; // Parent route key reference
  filePath: string;
  children?: string[];
  getName?: (args: { params?: Record<string, string | undefined>; loaderData?: any }) => string;
  feature?: string;
  staticLabel?: string;
}

// Define all possible roles
export enum PlatformRoleEnum {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

export enum PersonaRoleEnum {
  PRODUCTION_HEAD = 'PRODUCTION_HEAD',
  FIELD_AGENT = 'FIELD_AGENT',
  AGRONOMIST = 'Agronomist',
  FIELD_TECHNICIAN = 'FIELD_TECHNICIAN',
  AUDITOR = 'central_auditor',
  SUPERVISOR = 'SUPERVISOR',
  REGIONAL_MANAGER = 'REGIONAL_MANAGER',
  FULLFILMENT_MANAGER = 'fulfilment_manager',
  FULLFILMENT_OPERATOR = 'fulfilment_operator',
  LOGISTICS_OPERATOR = 'LOGISTICS_OPERATOR',
  BUSINESS_USER = 'BUSINESS_USER',
  BUSINESS_ADMIN = 'BUSINESS_ADMIN',
}

// First define route keys without 'as const' for TypeScript compatibility
// This ensures that we can properly reference the keys in the AppRoutes object
export enum AppRouteKey {
  LOGIN = 'LOGIN',
  SIGNUP = 'SIGNUP',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
  VERIFY_INVITE = 'VERIFY_INVITE',
  SETUP_ORGANIZATION = 'SETUP_ORGANIZATION',
  HOME = 'HOME',
  USER_MANAGEMENT = 'USER_MANAGEMENT',
  SUPPORT = 'SUPPORT',
  NOTIFICATIONS = 'NOTIFICATIONS',
  NOT_EXIST = 'NOT_EXIST',
  ACTIVE_UNDER_PLANNING = 'ACTIVE_UNDER_PLANNING',
  ACTIVE_PUBLISHED = 'ACTIVE_PUBLISHED',
  NO_ACCESS = 'NO_ACCESS',
  ROLES_MANAGEMENT = 'ROLES_MANAGEMENT',
  ORGANIZATION_SETTINGS = 'ORGANIZATION_SETTINGS',
  ORGANIZATION_PROFILE_SETTINGS = 'ORGANIZATION_PROFILE_SETTINGS',
  //AUDITOR_APP = 'AUDITOR_APP',
  AUDIT_ACTIVITY = 'AUDIT_ACTIVITY',
  AUDIT_FARMER = 'AUDIT_FARMER',
  AUDIT_SETTINGS = 'AUDIT_SETTINGS',
  AUDIT_PROFILE = 'AUDIT_PROFILE',
  TERMS_CONDITIONS = 'TERMS_CONDITIONS',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  FARMER_AUDIT = 'FARMER_AUDIT',
  AUDIT_FARMER_DETAIL = 'AUDIT_FARMER_DETAIL',
  //Production Head
  PRODUCTION_HEAD_SEASON = 'PRODUCTION_HEAD_SEASON',
  PRODUCTION_HEAD_SEASON_CREATE = 'PRODUCTION_HEAD_SEASON_CREATE',
  PRODUCTION_HEAD_SEASON_EDIT = 'PRODUCTION_HEAD_SEASON_EDIT',
  PRODUCTION_HEAD_SEASON_SETTINGS = 'PRODUCTION_HEAD_SEASON_SETTINGS',
  PRODUCTION_HEAD_SEASON_DETAIL = 'PRODUCTION_HEAD_SEASON_DETAIL',
  PRODUCTION_HEAD_DISTRIBUTE_TARGETS = 'PRODUCTION_HEAD_DISTRIBUTE_TARGETS',
  PRODUCTION_HEAD_SEASON_SETTINGS_VERSION_DETAILS = 'PRODUCTION_HEAD_SEASON_SETTINGS_VERSION_DETAILS',
  PRODUCTION_HEAD_SEASON_SETTINGS_SEASON_PLAN = 'PRODUCTION_HEAD_SEASON_SETTINGS_SEASON_PLAN',
  PRODUCTION_HEAD_SEASON_SETTINGS_SEASON_PLAN_VERSION = 'PRODUCTION_HEAD_SEASON_SETTINGS_SEASON_PLAN_VERSION',
  PRODUCTION_HEAD_SEASON_PLAN_VERSION_REVISION = 'PRODUCTION_HEAD_SEASON_PLAN_VERSION_REVISION',
  PRODUCTION_HEAD_OPERATIONAL_PLAN_VERSION_DETAIL = 'PRODUCTION_HEAD_OPERATIONAL_PLAN_VERSION_DETAIL',
  PRODUCTION_HEAD_HARVEST_PLAN = 'PRODUCTION_HEAD_HARVEST_PLAN',
  // Agronomist
  AGRONOMIST_SEASONS = 'AGRONOMIST_SEASONS',
  AGRONOMIST_SEASON_PLAN = 'AGRONOMIST_SEASON_PLAN',
  AGRONOMIST_SEASON_PLAN_SEASON = 'AGRONOMIST_SEASON_PLAN_SEASON',
  AGRONOMIST_SEASON_PLAN_SEASON_PLAN = 'AGRONOMIST_SEASON_PLAN_SEASON_PLAN',
  AGRONOMIST_SEASON_PLAN_SEASON_PLAN_VERSION = 'AGRONOMIST_SEASON_PLAN_SEASON_PLAN_VERSION',
  AGRONOMIST_SEASON_PLAN_SEASON_PLAN_VERSION_REVISION = 'AGRONOMIST_SEASON_PLAN_SEASON_PLAN_VERSION_REVISION',
  AGRONOMIST_SEASON_PLAN_CREATE = 'AGRONOMIST_SEASON_PLAN_CREATE',
  // Agronomist Operational Plan
  AGRONOMIST_OPERATIONAL_PLAN_INDEX = 'AGRONOMIST_OPERATIONAL_PLAN_INDEX',
  AGRONOMIST_OPERATIONAL_PLAN_LIST = 'AGRONOMIST_OPERATIONAL_PLAN_LIST',
  AGRONOMIST_OPERATIONAL_PLAN = 'AGRONOMIST_OPERATIONAL_PLAN',
  AGRONOMIST_OPERATIONAL_PLAN_VERSION = 'AGRONOMIST_OPERATIONAL_PLAN_VERSION',
  // Agronomist Harvest & Recovery
  AGRONOMIST_HARVEST_RECOVERY_INDEX = 'AGRONOMIST_HARVEST_RECOVERY_INDEX',
  AGRONOMIST_HARVEST_RECOVERY_LIST = 'AGRONOMIST_HARVEST_RECOVERY_LIST',
  AGRONOMIST_HARVEST_RECOVERY = 'AGRONOMIST_HARVEST_RECOVERY',
  AGRONOMIST_HARVEST_RECOVERY_VERSION = 'AGRONOMIST_HARVEST_RECOVERY_VERSION',
  //Farmer report
  FARMER_REPORT = 'FARMER_REPORT',
  FARMER_REPORT_DIVISION_REPORT = 'FARMER_REPORT_DIVISION_REPORT',
  FARMER_REPORT_MUNICIPALITY_REPORT = 'FARMER_REPORT_MUNICIPALITY_REPORT',
  FARMER_REPORT_VILLAGE_REPORT = 'FARMER_REPORT_VILLAGE_REPORT',
  FARMER_REPORT_ZONE_REPORT = 'FARMER_REPORT_ZONE_REPORT',
  FARMER_REPORT_CLUB_REPORT = 'FARMER_REPORT_CLUB_REPORT',
  FARMER_REPORT_REGION_MANAGER = 'FARMER_REPORT_REGION_MANAGER',
  FARMER_REPORT_SETTLEMENT_REPORT = 'FARMER_REPORT_SETTLEMENT_REPORT',
  // Season Report
  SEASON_REPORT = 'SEASON_REPORT',
  SEASON_REPORT_DETAIL = 'SEASON_REPORT_DETAIL',
  // Profile
  PROFILE = 'PROFILE',
  // User Report
  SUPERVISOR_USER_REPORT_BUSINESS_USER = 'SUPERVISOR_USER_REPORT_BUSINESS_USER',
  USER_REPORT_BUSINESS_USER = 'USER_REPORT_BUSINESS_USER',
  FIELD_TECH_USER_REPORT_BUSINESS_USER = 'FIELD_TECH_USER_REPORT_BUSINESS_USER',

  FARMER_REPORT_PLOTS = 'FARMER_REPORT_PLOTS',
  FARMER_REPORT_GLOBAL_MAP = 'FARMER_REPORT_GLOBAL_MAP',
  FARMER_REPORT_FARMER_360 = 'FARMER_REPORT_FARMER_360',
  USER_REPORT_REGION_MANAGER = 'USER_REPORT_REGION_MANAGER',
  // Contract Report
  CONTRACT_REPORT = 'CONTRACT_REPORT',
  CONTRACT_REPORT_SEASON_DETAIL = 'CONTRACT_REPORT_SEASON_DETAIL',
  CONTRACT_REPORT_REGIONAL_TARGET_DETAIL = 'CONTRACT_REPORT_REGIONAL_TARGET_DETAIL',
  // Business Admin
  BUSINESS_ADMIN = 'BUSINESS_ADMIN',
  BUSINESS_ADMIN_REGIONS = 'BUSINESS_ADMIN_REGIONS',
  BUSINESS_ADMIN_METRICS = 'BUSINESS_ADMIN_METRICS',
  BUSINESS_ADMIN_BILLING_ENTITY = 'BUSINESS_ADMIN_BILLING_ENTITY',
}

// Define the routes type
export type AppRoutesType = Record<AppRouteKey, RouteInfo>;
